/**
 * Spell checking with graduated help.
 *
 * The core is not finding mistakes — nspell does that. The core is HOW the
 * help is offered: trying must be faster and more convenient than having the
 * solution shown. Otherwise you only click "reveal" and learn nothing.
 *
 * That is why the order is fixed and the last stage is deliberately slow.
 */

import nspell from './nspell.js'
import type { NSpell } from './nspell.js'

/** Words that are not checked: numbers, abbreviations with a period. */
const SKIP = /^[\d.,:%€$-]+$/

/**
 * Merges headwords that occur more than once into one line.
 *
 * The de_DE dictionary lists many words twice — once as a verb, once as a
 * nominalized form, each with its own affix flags:
 *
 *   gehen/DIVXW     <- verb
 *   gehen/Sozm      <- "das Gehen", o = ONLYINCOMPOUND
 *
 * On the second occurrence nspell overwrites the first entry instead of
 * uniting the flags. Thus ONLYINCOMPOUND wins here, and "gehen" as a word on
 * its own suddenly counts as wrong. Around 3,900 headwords are affected,
 * among them gehen, lesen, rennen, schreiben — precisely the everyday verbs
 * that occur constantly in an essay.
 *
 * That is why the flag sets are united before loading.
 */
export function mergeDuplicates(dic: string, aff: string): string {
  // The guard also accepts a Buffer at runtime; the type says string.
  const text = typeof dic === 'string' ? dic : String(dic)
  const lines = text.split('\n')
  const order: string[] = []
  const flags = new Map<string, Set<string>>()

  // Flags that RESTRICT validity. If the same headword occurs once with and
  // once without such a restriction, the free reading wins: "gehen" is a
  // valid word, even though "das Gehen" only occurs in compounds.
  const restricting = restrictingFlags(aff)
  const unrestricted = new Set<string>()

  // Line 0 is the count and is written anew.
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i]
    if (!line || line.startsWith('\t') || line.startsWith(' ')) continue
    const slash = line.indexOf('/')
    const stem = slash < 0 ? line : line.slice(0, slash)
    const f = slash < 0 ? '' : line.slice(slash + 1)
    if (!flags.has(stem)) {
      flags.set(stem, new Set())
      order.push(stem)
    }
    // Set just above when missing, so the entry exists.
    for (const c of f) flags.get(stem)!.add(c)
    if (![...f].some((c) => restricting.has(c))) unrestricted.add(stem)
  }

  const out = order.map((stem) => {
    // Every stem in `order` was put into `flags` in the same step.
    let set = flags.get(stem)!
    // An unrestricted reading cancels the restrictions of the others.
    if (unrestricted.has(stem)) {
      set = new Set([...set].filter((c) => !restricting.has(c)))
    }
    const f = [...set].join('')
    return f ? `${stem}/${f}` : stem
  })
  return `${out.length}\n${out.join('\n')}\n`
}

/**
 * Reads from the affix file which flag characters restrict validity.
 *
 * The characters can be chosen freely per dictionary (in the German one they
 * are o, h, d), so they are read out instead of hard-wired.
 */
function restrictingFlags(aff: string): Set<string> {
  const out = new Set<string>()
  if (!aff) return out
  // The guard also accepts a Buffer at runtime; the type says string.
  const text = typeof aff === 'string' ? aff : String(aff)
  for (const key of ['ONLYINCOMPOUND', 'NEEDAFFIX', 'FORBIDDENWORD']) {
    const m = text.match(new RegExp(`^${key}\\s+(\\S+)`, 'm'))
    // Group 1 exists whenever the regex matches.
    if (m) out.add(m[1]!)
  }
  return out
}

export class Spellchecker {
  spell: NSpell
  personal: Set<string>
  cache: Map<string, boolean>

  constructor(aff: string, dic: string, personal: string[] = []) {
    this.spell = nspell(aff, mergeDuplicates(dic, aff))
    this.personal = new Set(personal)
    this.cache = new Map()
  }

  /** Adds a word to the personal dictionary (names, technical terms). */
  learn(word: string): string[] {
    this.personal.add(word)
    this.cache.delete(word)
    return [...this.personal]
  }

  forget(word: string): void {
    this.personal.delete(word)
    this.cache.delete(word)
  }

  /**
   * Is the word spelled correctly?
   *
   * The dictionary knows many verbs only in lower case ("schreiben") and many
   * nouns only in upper case ("Schreiben"). But at the start of a sentence a
   * verb is capitalized too. A plain lookup would complain about
   * "Schreiben wir!".
   *
   * Therefore a capitalized word also counts as correct when the lower-case
   * form is in the dictionary. NOT the other way round: "hund" stays wrong,
   * because writing nouns in lower case is a real mistake, and a relevant one
   * for Elmar.
   */
  correct(word: string): boolean {
    if (!word || SKIP.test(word)) return true
    // Guarded by `has`, so the entry is there.
    if (this.cache.has(word)) return this.cache.get(word)!

    let ok = this.personal.has(word) || this.spell.correct(word)

    if (!ok && isCapitalized(word)) {
      // isCapitalized() is only true for a non-empty word.
      const lower = word[0]!.toLowerCase() + word.slice(1)
      ok = this.personal.has(lower) || this.spell.correct(lower)
    }

    this.cache.set(word, ok)
    return ok
  }

  /**
   * Suggestions, cut down to the essentials.
   *
   * A long list is not help but a new task. Three suggestions can be
   * compared, ten are read by nobody.
   */
  suggest(word: string, limit = 3): string[] {
    const raw: string[] = []
    for (const s of this.spell.suggest(word)) {
      if (!raw.includes(s)) raw.push(s)
      if (raw.length >= 12) break
    }

    // Sort by similarity of sound instead of by edit distance.
    //
    // nspell orders by letter distance — for "Heuser" this puts "Heiser"
    // before "Häuser", and for "fare" it puts "fade" before "fahre". But
    // whoever writes by ear almost always means the word that sounds the
    // same. Therefore what sounds alike comes first.
    const key = soundalike(word)
    return raw
      .map((s) => ({ s, same: soundalike(s) === key }))
      .sort((a, b) => (b.same ? 1 : 0) - (a.same ? 1 : 0))
      .slice(0, limit)
      .map((x) => x.s)
  }

  /**
   * Splits text into checkable words with their position.
   *
   * Apostrophes and hyphens belong to the word ("Kinder-Uni", "geht's"),
   * everything else separates.
   */
  *words(text: string): Generator<{ word: string; start: number; end: number }> {
    const re = /[\p{L}\p{N}]+(?:[’'-][\p{L}\p{N}]+)*/gu
    let m
    while ((m = re.exec(text))) {
      yield { word: m[0], start: m.index, end: m.index + m[0].length }
    }
  }

  /** All misspelled words of a text. */
  check(text: string): { word: string; start: number; end: number }[] {
    const out: { word: string; start: number; end: number }[] = []
    for (const w of this.words(text)) {
      if (!this.correct(w.word)) out.push(w)
    }
    return out
  }
}

function isCapitalized(w: string): boolean {
  const c = w[0]
  if (!c) return false
  return c !== c.toLowerCase()
}

/**
 * Very rough phonetic transcription for German.
 *
 * Maps onto each other everything that sounds the same but is written
 * differently — precisely the pairs that get confused when writing by ear.
 * Not a complete algorithm like Kölner Phonetik, but only enough that
 * "Schuhle" and "Schule" get the same key and "Schuhe" does not.
 */
export function soundalike(w: string): string {
  return w
    .toLowerCase()
    .replace(/sch/g, 'S')
    .replace(/ch/g, 'C')
    .replace(/[aeiou]h/g, '$&')   // mark the lengthening h, remove it below
    .replace(/([aeiouäöü])h/g, '$1')
    .replace(/ie/g, 'i')
    .replace(/ä/g, 'e')
    .replace(/äu/g, 'eu')
    .replace(/ey|ai/g, 'ei')
    .replace(/v/g, 'f')
    .replace(/ß|ss/g, 's')
    .replace(/z|ts/g, 'z')
    .replace(/ck|k|q/g, 'k')
    .replace(/(.)\1+/g, '$1')    // contract double letters
    .replace(/[dt]$/g, 't')       // final-obstruent devoicing
    .replace(/[bp]$/g, 'p')
    .replace(/[gk]$/g, 'k')
}

/**
 * Is it clear WHICH word was meant?
 *
 * The card shows a place in the word and makes one wait for the answer. Both
 * only make sense when there is one: „schwimt" is plainly „schwimmt", and
 * pointing at the missing letter teaches something.
 *
 * For „Equater" the service offers „Ecuador", „Äquator", „Equiner" — none of
 * which sounds like what was written, and the first one is a country. Marking
 * a letter there claims a certainty nobody has, and a timer before revealing
 * makes a child wait for a guess.
 *
 * The measure is `soundalike`: a suggestion that sounds like the attempt is
 * the one that was meant. Exactly one of those, and the case is clear —
 * several spellings of the same word count as one, because choosing between
 * „Fahre" and „fahre" is a question of capitalisation, not of which word.
 */
export function isClear(word: string, suggestions: readonly string[]): boolean {
  if (!suggestions.length) return false
  const key = soundalike(word)
  const same = suggestions.filter((s) => soundalike(s) === key)
  if (!same.length) return false
  // „Fahre" and „fahre" are one answer, not two.
  return new Set(same.map((s) => s.toLowerCase())).size === 1
}

/**
 * The help stages.
 *
 * Every stage costs more than the one before — not in points, but in effort
 * and waiting time. "Nochmal" is there immediately and costs one click. The
 * solution has to be waited for.
 */
export const STAGES = [
  { id: 'retry', label: 'Nochmal versuchen', delay: 0 },
  { id: 'locate', label: 'Zeig mir die Stelle', delay: 0 },
  { id: 'rule', label: 'Gib mir einen Tipp', delay: 0 },
  { id: 'reveal', label: 'Lösung zeigen', delay: 12000 },
] as const

/**
 * Finds the first differing place between attempt and target.
 *
 * The comparison runs from the front and from the back; in between lies the
 * range in which the words differ. This hits the mistake even when a letter
 * is missing or superfluous — a plain positional comparison would mark
 * everything from the missing letter onwards.
 */
export function locateError(attempt: string, target: string): { start: number; end: number } {
  // Pure capitalization mistake: the difference is the first letter,
  // otherwise the comparison below would find no difference at all and would
  // mark the end of the word.
  if (attempt.toLowerCase() === target.toLowerCase() && attempt !== target) {
    for (let i = 0; i < attempt.length; i++) {
      if (attempt[i] !== target[i]) return { start: i, end: i + 1 }
    }
  }

  const a = attempt.toLowerCase()
  const b = target.toLowerCase()

  let head = 0
  while (head < a.length && head < b.length && a[head] === b[head]) head++

  let tail = 0
  while (
    tail < a.length - head &&
    tail < b.length - head &&
    a[a.length - 1 - tail] === b[b.length - 1 - tail]
  ) tail++

  return { start: head, end: Math.max(head, attempt.length - tail) }
}

/**
 * Rule hints.
 *
 * Deliberately phrased as a question: a hint that contains the answer is a
 * reveal by a detour. "Hörst du ein langes ie?" lets the child think for
 * itself, "Es heißt ie" does not.
 *
 * The rules are tested against the COMPARISON of attempt and solution, not
 * against the wrong word alone — only this way can it be said WHICH rule
 * applies here.
 */
const RULES: { id: string; test: (a: string, b: string) => boolean; hint: string }[] = [
  {
    id: 'ie',
    test: (a, b) => /ie/.test(b) && !/ie/.test(a) && /i/.test(a),
    hint: 'Sprich das Wort langsam. Hörst du ein langes „i“? Dann schreibt man oft ie.',
  },
  {
    id: 'ss-ß',
    test: (a, b) => (/ß/.test(b) && /ss/.test(a)) || (/ss/.test(b) && /ß/.test(a)),
    hint: 'Ist der Selbstlaut davor kurz oder lang? Nach kurzem Laut steht ss, nach langem ß.',
  },
  {
    id: 'doppel',
    test: (a, b) => hasDoubleConsonant(b) && !hasDoubleConsonant(a),
    hint: 'Sprich die Silben einzeln. Klingt der Selbstlaut kurz? Dann kommt der Mitlaut oft doppelt.',
  },
  {
    id: 'doppel-zuviel',
    test: (a, b) => hasDoubleConsonant(a) && !hasDoubleConsonant(b),
    hint: 'Ein Mitlaut ist hier doppelt. Sprich das Wort langsam — klingt der Selbstlaut davor wirklich kurz?',
  },
  {
    id: 'dehnungs-h',
    test: (a, b) => /[aeiouäöü]h/.test(b) && !/[aeiouäöü]h/.test(a),
    hint: 'Hier fehlt ein stiller Mitlaut, den man nicht hört, aber schreibt.',
  },
  {
    id: 'h-zuviel',
    test: (a, b) => /[aeiouäöü]h/.test(a) && !/[aeiouäöü]h/.test(b),
    hint: 'Ein Buchstabe ist zu viel — einer, den man gar nicht hört.',
  },
  {
    id: 'auslaut',
    test: (a, b) => endSwap(a, b, 'd', 't') || endSwap(a, b, 'b', 'p') || endSwap(a, b, 'g', 'k'),
    hint: 'Verlängere das Wort: Aus „Hund“ wird „Hunde“. Welchen Mitlaut hörst du dann am Ende?',
  },
  {
    id: 'äu-eu',
    test: (a, b) => (/äu/.test(b) && /eu/.test(a)) || (/eu/.test(b) && /äu/.test(a)),
    hint: 'Gibt es ein verwandtes Wort mit a oder au? Dann schreibt man äu.',
  },
  {
    id: 'gross',
    test: (a, b) => a.toLowerCase() === b.toLowerCase() && a !== b,
    hint: 'Kannst du „der“, „die“ oder „das“ davor setzen? Dann ist es ein Nomen und wird großgeschrieben.',
  },
  {
    id: 'dreher',
    test: (a, b) => a.length === b.length && sorted(a) === sorted(b),
    hint: 'Alle Buchstaben stimmen — nur zwei stehen vertauscht. Lies langsam von links nach rechts.',
  },
]

/**
 * Selects the matching rule hint. If no rule applies, there is a general hint
 * about the length — better than nothing, and still not a reveal.
 */
export function ruleHint(attempt: string, target: string): { id: string; hint: string } {
  for (const r of RULES) {
    if (r.test(attempt, target)) return { id: r.id, hint: r.hint }
  }
  const d = target.length - attempt.length
  if (d > 0) return { id: 'kurz', hint: `Es fehlt noch etwas — ${d === 1 ? 'ein Buchstabe' : `${d} Buchstaben`}.` }
  if (d < 0) return { id: 'lang', hint: `Da ${-d === 1 ? 'ist ein Buchstabe' : `sind ${-d} Buchstaben`} zu viel.` }
  return { id: 'gleich', hint: 'Die Länge stimmt. Welcher Buchstabe passt nicht?' }
}

function hasDoubleConsonant(w: string): boolean {
  return /([bcdfgklmnprstz])\1/.test(w.toLowerCase())
}

function endSwap(a: string, b: string, x: string, y: string): boolean {
  const la = a.toLowerCase(), lb = b.toLowerCase()
  return (la.endsWith(x) && lb.endsWith(y)) || (la.endsWith(y) && lb.endsWith(x))
}

function sorted(w: string): string {
  return [...w.toLowerCase()].sort().join('')
}
